import React, { useState, useEffect, useRef } from 'react'

import { Link, useNavigate, useLocation } from 'react-router-dom'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import update from 'immutability-helper'
import { Tooltip } from '@mui/material'
import { useAuth } from '../../hooks/auth'

import AppLayout from '../Layouts/AppLayout'
import { useTranslation } from 'react-i18next'
import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import '../../CollectionCalendar.css'
import Loading from '../Loading'
import { isValid } from '../../helpers/helper'
import Navigation from './Navigation'
import i18next from 'i18next'
import allLocales from '@fullcalendar/core/locales-all'
import { buildUrl } from '../../helpers/apiHelper'
import EventItems from '../common/calendar/EventItems'

const CollectionCalendar = () => {

    const { user } = useAuth({ middleware: 'guest ' })

    const { t } = useTranslation()
    const [orders, setOrders] = useState([])
    const { config, choosesite, setChoosesite, pusher } = useStateContext()
    const calendarRef = useRef([])
    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(false)

    const [events, setEvents] = useState([])

    useEffect(() => {
        // Unsubscribe on unmount
        return (() => {
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-created-site-${localStorage.getItem('site')}`)
            pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${localStorage.getItem('site')}`)
        })
    }, [])

    useEffect(() => {
        if(choosesite) {
            getOrders(choosesite)
        }
    }, [choosesite])

    useEffect(() => {
        if (isValid(choosesite)) {
            const channeldelete = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
            const channelcreate = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-created-site-${choosesite}`)
            const channelupdate = pusher.subscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)

            channeldelete.bind(`${localStorage.getItem('client_id')}-purchaseorder-deleted-event-site-${choosesite}`, data => {
                setOrders((prev) => {
                    const item = prev.find((i) => i.id === data.id)
                    const exItem = prev.filter((i) => i.id !== item.id)
                    return exItem
                })
            })

            channelcreate.bind(`${localStorage.getItem('client_id')}-purchaseorder-created-event-site-${choosesite}`, data => {
                getSalesOrder(data.id, 'created')
            })

            channelupdate.bind(`${localStorage.getItem('client_id')}-purchaseorder-updated-event-site-${choosesite}`, data => {
                getSalesOrder(data.id, 'updated')
            })
        }

        return (() => {
            if(isValid(choosesite)) {
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-deleted-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-created-site-${choosesite}`)
                pusher.unsubscribe(`${localStorage.getItem('client_id')}-purchaseorder-updated-site-${choosesite}`)
            }
        })
    }, [choosesite])

    useEffect(() => {
        setEvents([])
        orders.map((i) => {
            let new_item = {
                "order_id": i.id,
                "title": i.po_number,
                "date": i.requested_collection_date + 'T' + i.requested_collection_time_time,
                "requested_collection_date": i.requested_collection_date,
                "customer_name": i.vendor_name,
                "ship_address": i.purchase_address_name,
                "items": i?.purchase_order_items
            }

            setEvents((prev) => [...prev, new_item])
        })
    }, [orders])

    {/* Functions */}

    const getOrders = async (site) => {
        setEvents([])
        setIsLoading(true)

        const url = buildUrl(
            '/api/list-orders',
            {
                where: {
                    'order_type': 'purchase',
                    'site_id': site,
                    'pending': !localStorage.getItem('client_id').includes('bramptonpallet') ? true : ''
                }
            }
        )

        await axios.get(url, config)
            .then(res => {
                const orders = res.data?.data.filter((i) => {
                    const upTo = !localStorage.getItem('client_id').includes('bramptonpallet') ? 10 : 11;
                    return i.po_status_id < upTo;
                });
                setOrders(orders)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }

    const getSalesOrder = async (id, state) => {
        setIsLoading(true)
        await axios.get(`/api/purchase-orders/${id}`, config)
            .then(res => {
                const salesOrder = res.data
                if (state === 'created') setOrders((prev) => [...prev, salesOrder])

                if (state === 'updated') setOrders((prev) => {

                    const index = prev?.findIndex((i) => i.id === id)
                    if (index < 0) {
                        const items = [...prev, salesOrder]
                        return items
                    } else {
                        if (salesOrder.so_status === "shipped") {
                            return update(prev, { $splice: [[index, 1]] })
                        } else {
                            return update(prev,
                                { [index]: { $set: salesOrder } }
                            )
                        }
                    }
                })
                setIsLoading(false)
            })

    }

    const handleEventClick = (eventInfo) => {
        localStorage.setItem('initialDatePurchase', eventInfo.event.extendedProps.requested_collection_date)
        navigate(`/purchase-order/${eventInfo.event.extendedProps.order_id}`, { state: { route: '/purchase-order-calendar', prevPathname: location.pathname } });
    }

    return (
        <>
            {isLoading ? <Loading /> : null}
            <AppLayout>
                <Navigation route='purchase-order' type='calendar' user={user} />
                <div className='p-5 w-full'>
                    <div className='p-2 bg-white mb-2 rounded-md collection'>
                        <FullCalendar
                            locales={allLocales}
                            locale={i18next?.resolvedLanguage ?? 'en'}
                            plugins={[dayGridPlugin, timeGridPlugin]}
                            ref={calendarRef}
                            headerToolbar={{
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,timeGridWeek,timeGridDay'
                            }}
                            buttonText={{
                                today: t('today'),
                                month: t('month'),
                                week: t('week'),
                                day: t('day')
                            }}
                            customButtons={{
                                today: {
                                    text: t('today'),
                                    click: () => {
                                        localStorage.removeItem('initialDatePurchase')
                                        let calendarApi = calendarRef.current.getApi()
                                        calendarApi.today()
                                    }
                                }
                            }}
                            initialView='dayGridMonth'
                            events={events}
                            eventContent={renderEventContent}
                            dayMaxEvents={true}
                            eventMaxStack={1}
                            expandRows={true}
                            eventColor='#ffffff'
                            initialDate={localStorage.getItem('initialDatePurchase')}
                            eventClick={handleEventClick}
                        />
                    </div>
                </div>
            </AppLayout>
        </>
    )
}

export default CollectionCalendar

function renderEventContent(eventInfo) {

    const { items, order_id } = eventInfo.event.extendedProps;

    return (
        <Tooltip disableInteractive placement='left' title={
            <div className='flex flex-col'>
                <p className='text-[16px] pb-3'>{eventInfo.event.title}</p>
                <i className='text-[14px]'>{eventInfo.event.extendedProps.customer_name}</i>
                <i className='text-[14px]'>{eventInfo.event.extendedProps.ship_address}</i>
                <EventItems items={items} orderId={order_id} orderType="purchase" />
            </div>
        }>
            <button className='w-full'>
                <div className='p-1 flex flex-col text-left bg-[#b7472a] text-white rounded-sm'>
                    <p className='text-xl bold p-1 roboto'>{eventInfo.event.title}</p>
                    <i>{eventInfo.event.extendedProps.customer_name}</i>
                    <i>{eventInfo.event.extendedProps.ship_address}</i>
                    <EventItems items={items} orderId={order_id} orderType="purchase" />
                </div>
            </button>
        </Tooltip>
    )
}
